import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { useTranslation } from "react-i18next";
import moment from "moment";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import {
  DateFormat,
  getExtendedDateFormat,
  noDateDefinedOrEmpty,
} from "../../../modules/constants";
import { DEFAULT_MOMENT_LOCALE } from "../../../i18n/helpers";
import SmartTimePicker from "../../smart/DateAndTime/TimePicker/TimePicker";

const FeelingSickWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledDate = styled.p`
  margin-bottom: 2rem;
`;

const TimePickerContainer = styled.div`
  margin-bottom: 1rem;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const AddFeelingSick = (props) => {
  const { date, sickDayDate, onSubmit, onClose, submitting } = props;

  const [eventTime, setEventTime] = useState(moment().toDate());
  const [maxTime, setMaxTime] = useState(moment().toDate());
  const [dateTimeError, setDateTimeError] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    let tempTime = moment().toDate();

    if (sickDayDate && sickDayDate !== noDateDefinedOrEmpty) {
      tempTime = moment(sickDayDate).toDate();
    } else if (date) {
      const now = moment();
      tempTime = moment(date)
        .set({
          h: now.get("hour"),
          m: now.get("minute"),
        })
        .toDate();
    }

    setEventTime(tempTime);
  }, [sickDayDate, date]);

  useEffect(() => {
    if (!moment(eventTime).startOf("day").isSame(moment().startOf("day"))) {
      setMaxTime(moment(eventTime).endOf("day").toDate());
    }
  }, [eventTime]);

  const handleTimeChange = (value) => {
    setEventTime(value);
  };

  const onError = (error) => {
    setDateTimeError(error);
  };

  const submitForm = useCallback(() => {
    if (moment(eventTime).startOf("day").isSame(moment().startOf("day"))) {
      setMaxTime(moment().toDate());
    }

    const selectedDate = moment(
      sickDayDate === null || sickDayDate === noDateDefinedOrEmpty
        ? date
        : sickDayDate
    );
    const time = moment(eventTime).format("HH:mm").split(":");
    selectedDate.set({
      h: time[0],
      m: time[1],
    });

    const formData = {
      isSick: true,
      sickDayDate: selectedDate
        .locale(DEFAULT_MOMENT_LOCALE)
        .format(DateFormat.EXTENDED_DATETIME),
      reasonForChange: "",
    };

    onSubmit(formData);
  }, [eventTime, onSubmit, sickDayDate, date]);

  return (
    <FeelingSickWrapper>
      <StyledDate>{date.format(getExtendedDateFormat())}</StyledDate>
      <p>{t("appText.notFeelingWell.date.label")}</p>
      <TimePickerContainer>
        <SmartTimePicker
          displayTitle={false}
          onChange={handleTimeChange}
          value={eventTime}
          readOnly={false}
          maxTime={maxTime}
          onError={(error) => onError(error)}
          timeIntervals={5}
        />
      </TimePickerContainer>
      <AlertUGNX data-testid="alert-doctor" type={AlertType.WARNING}>
        {t("appText.notFeelingWell.info.message")}
      </AlertUGNX>
      <ActionButtonsContainer>
        <SecondaryButton
          data-testid="feeling-sick-cancel-button"
          className="me-2"
          onClick={onClose}
        >
          {t("appText.cancel")}
        </SecondaryButton>
        <PrimaryButton
          data-testid="feeling-sick-confirm-button"
          onClick={submitForm}
          disabled={dateTimeError || submitting}
        >
          {t("appText.confirm")}
        </PrimaryButton>
      </ActionButtonsContainer>
    </FeelingSickWrapper>
  );
};

export default AddFeelingSick;
